<template>
  <div>
    <!-- 기본정보 -->
    <field-select-module
      ref="fieldSelectModule"
      v-model="form.stdInfo"
      :board="this.board"
      :is-print="isPrint"
      :field-required="true"
      :options="[
        { type: 'date', label: '점검일', required: true },
        { type: 'text', label: '점검장소' },
        { type: 'disaster', label: '재해형태' },
      ]"
    />

    <!--    점검대상-->
    <vs-row class="mt-6">
      <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
        <h5><i class="dot" /> 점검대상</h5>
      </vs-col>
    </vs-row>
    <div class="grid grid-cols-12 gap-x-6 gap-y-4 mt-4">
      <div :class="isPrint ? 'col-span-2' : 'col-span-12 xl:col-span-2'" class="flex items-center" v-for="checkbox in form.inspectList" v-bind:key="checkbox.id">
        <div v-if="isPrint || board.readonly" class="flex">
          <div v-if="checkbox.check" style="background: #6670cb" class="w-5 h-5 rounded-sm text-white flex justify-center items-center mr-2">
            <vs-icon icon="check" size="x-small" style="background-color: #6670cb"></vs-icon>
          </div>
          <div v-else style="border: 1px solid #200c63" class="w-5 h-5 rounded-sm text-white flex justify-center items-center mr-2">
            <vs-icon icon="check" size="x-small"></vs-icon>
          </div>
          {{ checkbox.label }}
        </div>
        <label v-else class="flex">
          <vs-checkbox color="secondary" :readonly="board.readonly" v-model="checkbox.check" @change="checkForm"> </vs-checkbox>
          {{ checkbox.label }}
        </label>
      </div>
    </div>

    <!--    표-->
    <template v-for="(inspection, index) in form.inspectList">
      <TableInspectMerge
        :key="`inspection_${inspection.title}`"
        v-show="inspection.check"
        class="mt-5"
        :is-print="isPrint"
        :is-readonly="board.readonly"
        :title="inspection.title"
        :tableHeaderTitle="inspection.tableHeaderTitle ? inspection.tableHeaderTitle : '점검항목'"
        :value="getListData(inspection)"
        @input="(v) => setListData(inspection, v)"
        :table-header-note="[{ text: '비고(보완계획)', width: '389' }]"
        :table-header-check="getHeaderCheck(inspection.title)"
        :only-pc="isPrint"
        contentBefore="•"
      >
        <template #customHead>
          <tr>
            <th rowspan="2" colspan="2">점검항목</th>
            <th :colspan="getHeaderCheck(inspection.title).length">점검결과</th>
            <th rowspan="2">비고(보완계획)</th>
          </tr>
          <tr>
            <th v-for="cb in getHeaderCheck(inspection.title)" :key="`head-${cb.text}`">{{ cb.text }}</th>
          </tr>
        </template>
      </TableInspectMerge>

      <!-- 체감온도: 휴게시설(id=9) 테이블 뒤, 온열질환 테이블 앞에 삽입 -->
      <div v-if="inspection.id === 9 && form.inspectList[9] && form.inspectList[9].check" :key="`temp_feel_section`">
        <vs-row class="mt-6">
          <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
            <h5><i class="dot" /> 체감온도</h5>
            <vx-tooltip title="체감온도 산출표" :html="'자세한 정보를 보려면 클릭하세요'" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
              <div style="color: #7e72f2; cursor: pointer" class="flex flex-row items-center ml-4" @click="showPopup = true">
                <feather-icon icon="AlertCircleIcon" style="width: 18px" />
                <span class="ml-2" style="font-size: 14px">체감온도 산출표</span>
              </div>
            </vx-tooltip>
          </vs-col>
        </vs-row>

        <div class="w-full mt-4" style="border: 1px solid #cdcdcd; padding: 21px; font-size: 14px">
          <div class="flex flex-row items-center gap-4">
            <div>온도(℃)</div>
            <div><number-input v-model="form.temperature" :max="40" :min="-20" /></div>
            <div class="ml-3">습도(%)</div>
            <div><number-input v-model="form.humidity" :max="100" :min="0" /></div>
          </div>
          <div class="flex flex-row items-center gap-4 mt-4">
            <div>체감온도 :</div>
            <div style="font-size: 18px; font-weight: 700; color: #3c3c3c">{{ form.temperature_feel }}℃</div>
            <div v-if="form.temperature_feel >= 38" class="ml-3 temp-tag" style="background: #ea5455">위험</div>
            <div v-else-if="form.temperature_feel >= 35" class="ml-3 temp-tag" style="background: #ff9f43">경고</div>
            <div v-else-if="form.temperature_feel >= 33" class="ml-3 temp-tag" style="background: #ffcd43">주의</div>
            <div v-else-if="form.temperature_feel >= 31" class="ml-3 temp-tag" style="background: #00cfe8">관심</div>
          </div>
        </div>
      </div>
    </template>

    <!--    기타점검내용-->
    <table class="mt-5">
      <tr>
        <th>점검내용</th>
      </tr>
      <tr>
        <td>
          <span v-if="isPrint" class="print-textarea"> {{ form.기타점검내용 }} </span>
          <ml-textarea v-else class="w-full" rows="5" v-model="form.기타점검내용" :readonly="board.readonly" />
        </td>
      </tr>
    </table>

    <!-- 현장근로자 의견 청취 및 특이사항 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i>현장근로자 의견 청취 및 특이사항</h5>
      <div class="writeGridContainer">
        <div class="writeGridLineItem">
          <div style="line-height: 1.4; word-break: keep-all">현장근로자 의견 청취</div>
          <p v-if="isPrint" class="print-input">
            {{ form.inputC_1.fileName }}
          </p>
          <file-uploader v-else :readonly="board.readonly" v-model="form.inputC_1" />
        </div>
        <div class="writeGridLineItem">
          <div>특이사항</div>
          <span v-if="isPrint" class="print-textarea"> {{ form.inputC_2 }} </span>
          <ml-textarea v-else rows="5" v-model="form.inputC_2" :readonly="board.readonly" />
        </div>
      </div>
    </div>

    <!-- 유해·위험요인 개선 결과 -->
    <ImprovementTable :readonly="board.readonly" :isPrint="isPrint" v-model="form.개선결과" />

    <!-- 파일첨부 -->
    <file-upload-module id="fileList" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>

    <!-- 체감온도 산출표 팝업 컴포넌트 -->
    <TemperatureFeelTablePopup :visible.sync="showPopup" />
  </div>
</template>

<script>
import FileUploadModule from "@/views/modules/FileUploadModule";
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import PhotoUploadGridModule from "@/views/modules/PhotoUploadGridModule";
import FileUploader from "@/components/FileUploader";
import TableInspectEp from "@/components/TableInspectCustomEp";
import TableInspectMerge from "@/components/TableInspectMerge.vue";
import ImprovementTable from "./improvementTable.vue";
import NumberInput from "@/components/NumberInput.vue";
import TemperatureFeelTablePopup from "@/views/boards/single-page/198/TemperatureFeelTablePopup.vue";

import inspectData from "./inspectData";
import { createInspectListMigrator } from "@/util/InspectListMigration";
import * as UiUtil from "@/util/UiUtil";

// 카테고리 간 항목 이동 매핑 정의
const crossCategoryMapping = [
  { from: { categoryId: 2, itemId: 2 }, to: { categoryId: 5, itemId: 5 } }
];

// 마이그레이터 인스턴스 생성
const inspectListMigrator = createInspectListMigrator(inspectData, crossCategoryMapping);

export default {
  components: {
    TableInspectMerge,
    TableInspectEp,
    FileUploader,
    PhotoUploadGridModule,
    FieldSelectModule,
    FileUploadModule,
    ImprovementTable,
    NumberInput,
    TemperatureFeelTablePopup
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  data() {
    return {
      // 점검표별 점검결과 옵션 - 정의 없는 타이틀은 기본값(양호/미흡/해당없음) 사용
      // value는 저장 호환성을 위해 'Yes'/'No' 유지, 표시 text만 타이틀별로 다름
      INSPECTION_CHECK_OPTIONS: {
        온열질환: [
          { text: "적정", value: "Yes", width: "82" },
          { text: "개선필요", value: "No", width: "82" },
          { text: "해당없음", value: "N/A", width: "82" },
        ],
        휴게시설: [
          { text: "적정", value: "Yes", width: "82" },
          { text: "개선필요", value: "No", width: "82" },
          { text: "해당없음", value: "N/A", width: "82" },
        ],
      },
      DEFAULT_CHECK_OPTIONS: [
        { text: "양호", value: "Yes", width: "82" },
        { text: "미흡", value: "No", width: "82" },
        { text: "해당없음", value: "N/A", width: "82" },
      ],
      // 'N/A' 옵션이 없는 점검표에서 구 데이터의 'N/A'를 ''로 정리하기 위한 타이틀 목록
      TWO_OPTION_TITLES: ["온열질환", "휴게시설"],
      // (title, key) - 공백 포함 타이틀은 key에서 공백 제거하여 form 변수명으로 사용
      INSPECTION_TITLE_KEYS: [
        { title: "보호구", key: "보호구" },
        { title: "환기 및 배기장치", key: "환기및배기장치" },
        { title: "표지", key: "표지" },
        { title: "작업자세", key: "작업자세" },
        { title: "작업장", key: "작업장" },
        { title: "근로자", key: "근로자" },
        { title: "교육", key: "교육" },
        { title: "직업성 질환", key: "직업성질환" },
        { title: "휴게시설", key: "휴게시설" },
        { title: "온열질환", key: "온열질환" },
        { title: "한랭질환", key: "한랭질환" },
        { title: "밀폐공간", key: "밀폐공간" },
        { title: "MSDS", key: "MSDS" },
      ],
      showPopup: false,
      form: {
        stdInfo: {},
        inputC_1: {},
        inputC_2: "",

        typeTitleList: [],
        inspectList: [],
        photoList: [],
        fileList: [],

        temperature: 0,
        humidity: 0,
        temperature_feel: 0,

        개선결과: [],

        기타점검내용: "",

        // 점검표별 list - inspectList의 메타(id/label/title/check)만 남기고 list는 여기로 분리
        보호구: [],
        환기및배기장치: [],
        표지: [],
        작업자세: [],
        작업장: [],
        근로자: [],
        교육: [],
        직업성질환: [],
        휴게시설: [],
        온열질환: [],
        한랭질환: [],
        밀폐공간: [],
        MSDS: [],
      },

      inspectTable: JSON.parse(JSON.stringify(inspectData)),
    };
  },
  computed: {
    postId() {
      return this.$route.params.postId;
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        if (!value) return;

        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (this.postId && key === "개선결과" && value[key]) {
            value[key].forEach((item) => {
              const 원본문자 = item.개선내용;

              // 담당자와 개선일자 있는지 검사
              const has담당자 = 원본문자.includes("- 담당자");
              const has개선일자 = 원본문자.includes("- 개선일자");

              if (has담당자 && has개선일자) {
                console.log("원본", 원본문자);
                // 값 추출
                const 담당자Match = 원본문자.match(/- 담당자\s*:(.*)/);
                const 개선일자Match = 원본문자.match(/- 개선일자\s*:(.*)/);
                const 개선내용Match = 원본문자.match(/- 개선내용\s*:?\s*([\s\S]*?)(?:- 개선일자|$)/);

                const 담당자 = 담당자Match ? 담당자Match[1].trim() : null;
                const 개선일자 = 개선일자Match ? 개선일자Match[1].trim() : null;
                const 개선내용 = 개선내용Match ? 개선내용Match[1].trim() : "";

                item.개선내용담당자 = 담당자;
                item.개선내용개선일자 = 개선일자;
                item.개선내용 = 개선내용;
              }
            });
          }

          if (value[key]) {
            // inspectList는 별도 처리 - 항상 최신 템플릿 구조 사용, 체크 상태만 복원
            if (key === 'inspectList') {
              if (!value[key] || value[key].length === 0) {
                this.form[key] = inspectListMigrator.createEmpty();
              } else if (inspectListMigrator.needsMigration(value[key])) {
                // 마이그레이션이 필요한 경우에만 실행 (무한 루프 방지)
                this.form[key] = inspectListMigrator.migrate(value[key]);
              } else {
                this.form[key] = value[key];
              }
            } else {
              this.form[key] = value[key];
            }
          }
        }

        // inspectList가 없는 경우 초기화
        if (!this.form.inspectList || this.form.inspectList.length === 0) {
          this.form.inspectList = inspectListMigrator.createEmpty();
        }

        // 모든 점검표 list를 form 변수로 분리 (inspectList[i].list → form[titleToKey(title)])
        // 매번 호출하지만 변경이 없으면 $set 안 함 (idempotent → 무한 루프 방지)
        this.migrateAllInspectionLists(value);
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        value.title = this.board.writer.department;
        this.$emit("input", value);
      },
    },
    // 온도나 습도가 변경될 때마다 체감온도를 실시간으로 계산
    "form.temperature"() {
      this.calculateTemperatureFeel();
    },
    "form.humidity"() {
      this.calculateTemperatureFeel();
    },
  },
  mounted() {
    // 컴포넌트 마운트 시 체감온도 계산
    this.calculateTemperatureFeel();
  },
  methods: {
    // 점검결과 컬럼 구성 - INSPECTION_CHECK_OPTIONS에 정의된 타이틀은 그 옵션, 없으면 기본
    getHeaderCheck(title) {
      return this.INSPECTION_CHECK_OPTIONS[title] || this.DEFAULT_CHECK_OPTIONS;
    },
    // title → form 변수 key 매핑
    titleToKey(title) {
      const found = this.INSPECTION_TITLE_KEYS.find((m) => m.title === title);
      return found ? found.key : null;
    },
    // 점검표 list의 v-model 양방향용 - 모든 점검표가 form[key]를 sole source of truth로 사용
    getListData(inspection) {
      const key = this.titleToKey(inspection.title);
      if (key && Array.isArray(this.form[key])) return this.form[key];
      return inspection.list || [];
    },
    setListData(inspection, newValue) {
      const key = this.titleToKey(inspection.title);
      if (key) {
        this.$set(this.form, key, newValue);
      } else {
        inspection.list = newValue;
      }
    },
    // 점검표 list 한 건을 최신 template에 재정렬 (id 매칭으로 title/label 갱신, is_checked/note 보존)
    // - template에 없는 id는 제외
    // - template에만 있는 id는 빈 row로 추가
    // - 해당 점검표의 옵션에 없는 is_checked 값은 ''로 정리
    // - note는 TableInspectMerge가 "비고(보완계획)" 키에 v-model하므로 그 키를 우선 사용 (saved.note는 fallback)
    syncListWithTemplate(savedList, templateList, validValueSet) {
      const NOTE_KEY = "비고(보완계획)";
      const savedMap = {};
      for (const row of savedList || []) {
        if (row && row.id !== undefined) savedMap[row.id] = row;
      }
      return templateList.map((tplRow) => {
        const saved = savedMap[tplRow.id];
        let is_checked = saved ? saved.is_checked || "" : "";
        const noteValue = saved ? saved[NOTE_KEY] || saved.note || "" : "";
        if (is_checked && !validValueSet.has(is_checked)) is_checked = "";
        return {
          ...tplRow,
          is_checked,
          note: noteValue,
          [NOTE_KEY]: noteValue,
        };
      });
    },
    // 두 list가 (id/title/label/is_checked/note 기준) 동일한지 비교
    isSameInspectList(a, b) {
      const NOTE_KEY = "비고(보완계획)";
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        const x = a[i] || {};
        const y = b[i] || {};
        if (x.id !== y.id) return false;
        if (x.title !== y.title) return false;
        if (x.label !== y.label) return false;
        if ((x.is_checked || "") !== (y.is_checked || "")) return false;
        const xNote = x[NOTE_KEY] || x.note || "";
        const yNote = y[NOTE_KEY] || y.note || "";
        if (xNote !== yNote) return false;
      }
      return true;
    },
    // 모든 점검표 list를 form 변수로 동기화 (idempotent - 매번 호출 가능)
    // 우선순위:
    // 1) value[key]에 신규 형식 저장돼 있으면 사용 (template과 재정렬)
    // 2) 기존 inspectList[i].list에 데이터 있으면 사용 (template과 재정렬, 구 데이터 마이그레이션)
    // 3) form[key]가 이미 데이터 보유하고 있으면 유지 (사용자 입력 후 재호출 케이스)
    // 4) 위 모두 없으면 inspectData 템플릿으로 초기화
    // 결과가 form[key]와 동일하면 $set 안 함 → 무한 루프 방지
    migrateAllInspectionLists(value) {
      for (const { title, key } of this.INSPECTION_TITLE_KEYS) {
        const template = inspectData.find((item) => item && item.title === title);
        const templateList = template && Array.isArray(template.list) ? template.list : [];
        const options = this.getHeaderCheck(title);
        const validValueSet = new Set(options.map((o) => o.value));

        let sourceList = null;
        if (value && Array.isArray(value[key]) && value[key].length > 0) {
          sourceList = value[key];
        } else {
          // value.inspectList를 직접 사용 (form.inspectList는 inspectListMigrator가
          // "비고(보완계획)" 키를 드롭한 상태라 note 손실됨)
          const legacyItem = value && Array.isArray(value.inspectList)
            ? value.inspectList.find((item) => item && item.title === title)
            : null;
          if (legacyItem && Array.isArray(legacyItem.list) && legacyItem.list.length > 0) {
            sourceList = legacyItem.list;
          } else if (Array.isArray(this.form[key]) && this.form[key].length > 0) {
            // 이미 form에 데이터 있고 소스 없으면 유지 (사용자 입력 보존)
            continue;
          }
        }

        const migrated = sourceList
          ? this.syncListWithTemplate(sourceList, templateList, validValueSet)
          : JSON.parse(JSON.stringify(templateList));

        if (!this.isSameInspectList(this.form[key], migrated)) {
          this.$set(this.form, key, migrated);
        }
      }

      // 주의: inspectList[i].list는 비우지 않음
      // - inspectListMigrator.needsMigration이 빈 list를 마이그레이션 대상으로 판단하여
      //   이후 watch 시 list를 template으로 재채움 → 다시 비움 → 무한 루프 발생
      // - inspectList의 list는 form[key]가 sole source of truth이므로 데이터 표시/저장에 사용 안 됨
      //   (메타: id/label/title/check/tableHeaderTitle만 활용)
    },
    checkForm() {
      this.form.typeTitleList = [];
      for (const data of this.form.inspectList) {
        if (data.check) {
          this.form.typeTitleList.push(data.label);
        }
      }
    },
    updateListCheck(indexList, indexTableRows, text) {
      this.form.inspectList[indexList].list[indexTableRows].is_checked = text;
    },

    /**
     * 습구온도(Tw) 계산
     * @param {number} ta 기온 (℃)
     * @param {number} rh 상대습도 (%)
     * @returns {number} 습구온도
     */
    getTw(ta, rh) {
      // 습구온도 계산공식 (Java 코드와 동일)
      return ta * Math.atan(0.151977 * Math.pow(rh + 8.313659, 0.5)) + Math.atan(ta + rh) - Math.atan(rh - 1.67633) + 0.00391838 * Math.pow(rh, 1.5) * Math.atan(0.023101 * rh) - 4.686035;
    },

    /**
     * 여름철 체감온도 계산 (5월 ~ 9월)
     * @param {number} ta 기온 (℃)
     * @param {number} rh 상대습도 (%)
     * @returns {number} 체감온도
     */
    getInSummer(ta, rh) {
      const tw = this.getTw(ta, rh);
      return -0.2442 + 0.55399 * tw + 0.45535 * ta - 0.0022 * Math.pow(tw, 2.0) + 0.00278 * tw * ta + 3.0;
    },

    /**
     * 겨울철 체감온도 계산 (10월 ~ 익년 4월)
     * @param {number} ta 기온 (℃)
     * @param {number} v 10분 평균 풍속 (m/s) - 풍속이 없는 경우 0.5를 기본값으로 사용
     * @returns {number} 체감온도
     */
    getInWinter(ta, v = 0.5) {
      return 13.12 + 0.6215 * ta - 11.37 * Math.pow(v, 0.16) + 0.3965 * Math.pow(v, 0.16) * ta;
    },

    /**
     * 현재 월수 출력
     * @returns {number} 현재 월 (1-12)
     */
    getCurrentSeason() {
      return new Date().getMonth() + 1; // getMonth()는 0부터 시작하므로 +1
    },

    /**
     * 값의 범위를 제한하는 함수
     * @param {number} value 입력값
     * @param {number} min 최소값
     * @param {number} max 최대값
     * @returns {number} 제한된 값
     */
    clampValue(value, min, max) {
      return Math.max(min, Math.min(max, value));
    },

    /**
     * 체감온도 계산 (실시간)
     * 지금이 몇월인지에 따라 여름 및 겨울 계산공식 적용
     */
    calculateTemperatureFeel() {
      let ta = parseFloat(this.form.temperature) || 0;
      let rh = parseFloat(this.form.humidity) || 0;

      // 온도와 습도 값을 범위 내로 제한
      ta = this.clampValue(ta, -20, 40); // 온도: -20℃ ~ 40℃
      rh = this.clampValue(rh, 0, 100); // 습도: 0% ~ 100%

      // 제한된 값을 다시 form에 적용
      if (this.form.temperature !== ta) {
        this.form.temperature = ta;
      }
      if (this.form.humidity !== rh) {
        this.form.humidity = rh;
      }

      // 유효하지 않은 값인 경우 체감온도를 0으로 설정
      if (ta < -20 || ta > 40 || rh < 0 || rh > 100) {
        this.form.temperature_feel = 0;
        return;
      }

      // 현재 계절에 따라 체감온도 계산 공식 적용
      const season = this.getCurrentSeason();
      let temperatureFeel = 0;

      if (season >= 5 && season <= 9) {
        // 여름철 체감온도 계산 (5월~9월)
        temperatureFeel = this.getInSummer(ta, rh);
      } else {
        // 겨울철 체감온도 계산 (10월~4월)
        // 풍속이 없으므로 기본값 0.5 m/s 사용
        temperatureFeel = this.getInWinter(ta, 0.5);
      }

      this.form.temperature_feel = parseFloat(temperatureFeel.toFixed(1));
    },

    // 저장 전 필수값 검증 (통과 true / 실패 false). 사업장만 고르고 비운 채 저장되는 것을 막는다
    validate() {
      if (!this.form.stdInfo || !this.form.stdInfo.field_name) {
        const fieldTitle = (this.$refs.fieldSelectModule && this.$refs.fieldSelectModule.fieldTitle) || "사업장명";
        UiUtil.alertDialog(`${fieldTitle}을(를) 반드시 입력해야 합니다`);
        return false;
      }
      if (!this.form.stdInfo["점검일"]) {
        UiUtil.alertDialog("점검일을 반드시 입력해야 합니다");
        return false;
      }
      return true;
    },
  },
};
</script>

<style scoped lang="scss">
table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #cdcdcd;
    font-size: 14px;
  }
  th {
    font-weight: 500;
    text-align: center;
    background: #f9f9f9;
    padding: 8px;
  }
  td {
    font-weight: 400;
    padding: 4px;
  }
}
.temp-tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  color: #ffffff;
}
</style>
